"use client";

import { useLazyQuery } from "@apollo/client/react";
import { decrypt, identity } from "deso-protocol";
import type { ReactNode } from "react";
import { useCallback, useContext, useEffect, useState } from "react";
import { LuLockOpen as LuUnlock } from "react-icons/lu";

import EmbedPlayer from "@/components/app/post/embed-player";
import { PostVideoSection } from "@/components/app/post/post-video-section";
import { useIsLoggedIn } from "@/components/shared/is-logged-in";
import { PostAudioPlayer } from "@/components/shared/media-player/post-audio-player";
import { useGatedContent } from "@/contexts/gated-content-context";
import type { DecryptedGatedContentCacheEntry } from "@/contexts/post-cache-context";
import { usePostCacheContext } from "@/contexts/post-cache-context";
import { usePostContext } from "@/contexts/post-context";
import { UserContext } from "@/contexts/user-context";
import { GetAccessGroupMemberEncryptedPrivateKeyDocument } from "@/graphql/codegen/graphql";
import { DEFAULT_MESSAGING_GROUP_KEY_NAME } from "@/utils/constants";
import { formatUSDCents } from "@/utils/currency";
import { isAbortError, parseError, pluralize } from "@/utils/helpers";
import type { PostGatedContentSchema } from "@/utils/post-utils";
import { parsePostGatedContent } from "@/utils/post-utils";
import { AccessGroupType, getAccessGroupType } from "@/utils/subscriptions";
import type { PartialAccount } from "@/utils/types";
import { getUserName } from "@/utils/usernames";

import { PostImageSection } from "../../shared/post-image/post-image-section";
import { Badge } from "../../ui/badge";
import { Skeleton } from "../../ui/skeleton";
import { GatedContentDialog } from "../gated-content-dialog";
import { PostBody } from "./post-body";
import { PostPoll } from "./post-poll/post-poll";

interface PostGatedContentProps {
  creator: PartialAccount;
  children?: (props: {
    gatedContent: PostGatedContentSchema | null;
    decryptedPostBody: string | null;
    decryptedImageURLs: string[];
    decryptedVideoURLs: string[];
    decryptedAudioURLs: string[];
    isPendingContentDecryption: boolean;
    viewerIsSubscribed: boolean;
    gatedContentDecryptError: string | null;
    pollData: {
      PollWeightType: string;
      PollOptions: string;
      PollWeightTokenPublicKey: string;
    } | null;
  }) => ReactNode;
}

export const PostGatedContent = ({
  creator,
  children,
}: PostGatedContentProps) => {
  const { post, isPureRepost, repost, mediaMeta, postCachePrefix } =
    usePostContext();
  const isLoggedIn = useIsLoggedIn();

  const postHash = isPureRepost ? repost?.postHash : post?.postHash;
  const postExtraData = isPureRepost ? repost?.extraData : post?.extraData;

  if (!postHash) {
    throw new Error("Post hash is missing.");
  }

  const { decryptedGatedContentCache } = usePostCacheContext(
    postHash,
    postCachePrefix,
  );
  const { loading: userLoading, currentUserPublicKey } =
    useContext(UserContext);
  const cacheKey = `${postHash}-${currentUserPublicKey}`;
  const cacheEntry = decryptedGatedContentCache.get(cacheKey);
  const [gatedContent, setGatedContent] =
    useState<PostGatedContentSchema | null>(
      parsePostGatedContent(postExtraData),
    );

  const { subscriptions, getDecryptionSeedHex, addDecryptionSeedHexes } =
    useGatedContent();
  const [getEncryptedPrivateKey] = useLazyQuery(
    GetAccessGroupMemberEncryptedPrivateKeyDocument,
    {
      fetchPolicy: "network-only",
    },
  );
  const [viewerIsSubscribed, setViewerIsSubscribed] = useState(
    cacheEntry?.isSubscribed ?? false,
  );
  const [decryptedPostBody, setDecryptedPostBody] = useState<string | null>(
    cacheEntry?.postBody ?? null,
  );
  const [decryptedImageURLs, setDecryptedImageURLs] = useState<string[]>(
    cacheEntry?.imageURLs ?? [],
  );
  const [decryptedVideoURLs, setDecryptedVideoURLs] = useState<string[]>(
    cacheEntry?.videoURLs ?? [],
  );
  const [decryptedAudioURLs, setDecryptedAudioURLs] = useState<string[]>(
    cacheEntry?.audioURLs ?? [],
  );
  const [isPendingContentDecryption, setIsPendingContentDecryption] =
    useState(!cacheEntry);
  const [gatedContentDecryptError, setGatedContentDecryptError] = useState<
    string | null
  >(null);
  const [pollData, setPollData] = useState<{
    PollWeightType: string;
    PollOptions: string;
    PollWeightTokenPublicKey: string;
  } | null>(cacheEntry?.pollData ?? null);

  useEffect(() => {
    if (!isLoggedIn || !currentUserPublicKey || !gatedContent) {
      setViewerIsSubscribed(false);
      setDecryptedPostBody(null);
      setDecryptedImageURLs([]);
      setDecryptedVideoURLs([]);
      setDecryptedAudioURLs([]);
      setPollData(null);
      setIsPendingContentDecryption(false);
      setGatedContentDecryptError(null);
      return;
    }

    if (!cacheEntry) {
      setViewerIsSubscribed(false);
      setDecryptedPostBody(null);
      setDecryptedImageURLs([]);
      setDecryptedVideoURLs([]);
      setDecryptedAudioURLs([]);
      setPollData(null);
      setIsPendingContentDecryption(true);
      setGatedContentDecryptError(null);
      return;
    }

    setViewerIsSubscribed(cacheEntry.isSubscribed ?? false);
    setDecryptedPostBody(cacheEntry.postBody ?? null);
    setDecryptedImageURLs(cacheEntry.imageURLs ?? []);
    setDecryptedVideoURLs(cacheEntry.videoURLs ?? []);
    setDecryptedAudioURLs(cacheEntry.audioURLs ?? []);
    setPollData(cacheEntry.pollData ?? null);
    setIsPendingContentDecryption(false);
    setGatedContentDecryptError(null);
  }, [cacheEntry, cacheKey, currentUserPublicKey, gatedContent, isLoggedIn]);

  const decryptContent = useCallback(
    async (seedHex: string) => {
      if (!gatedContent) {
        throw new Error("No gated content to decrypt");
      }

      const promises = [];

      const newCacheEntry =
        decryptedGatedContentCache.get(cacheKey) ??
        ({} as DecryptedGatedContentCacheEntry);

      if (gatedContent.SubscriptionEncryptedPostBody.length > 0) {
        promises.push(
          decrypt(seedHex, gatedContent.SubscriptionEncryptedPostBody).then(
            (postBody) => {
              newCacheEntry.postBody = postBody;
              setDecryptedPostBody(postBody);
            },
          ),
        );
      }

      if (gatedContent.SubscriptionEncryptedImageURLs.length > 0) {
        promises.push(
          decrypt(seedHex, gatedContent.SubscriptionEncryptedImageURLs).then(
            (imageURLs) => {
              const parsed = JSON.parse(imageURLs);
              newCacheEntry.imageURLs = parsed;
              setDecryptedImageURLs(parsed);
            },
          ),
        );
      }

      if (gatedContent.SubscriptionEncryptedVideoURLs.length > 0) {
        promises.push(
          decrypt(seedHex, gatedContent.SubscriptionEncryptedVideoURLs).then(
            (videoURLs) => {
              const parsed = JSON.parse(videoURLs);
              newCacheEntry.videoURLs = parsed;
              setDecryptedVideoURLs(parsed);
            },
          ),
        );
      }

      if (gatedContent.SubscriptionEncryptedAudioURLs.length > 0) {
        promises.push(
          decrypt(seedHex, gatedContent.SubscriptionEncryptedAudioURLs).then(
            (audioURLs) => {
              const parsed = JSON.parse(audioURLs);
              newCacheEntry.audioURLs = parsed;
              setDecryptedAudioURLs(parsed);
            },
          ),
        );
      }

      if (gatedContent.SubscriptionEncryptedPollWeightType) {
        promises.push(
          Promise.all([
            decrypt(seedHex, gatedContent.SubscriptionEncryptedPollWeightType),
            gatedContent.SubscriptionEncryptedPollOptions
              ? decrypt(seedHex, gatedContent.SubscriptionEncryptedPollOptions)
              : "",
            gatedContent.SubscriptionEncryptedPollWeightTokenPublicKey
              ? decrypt(
                  seedHex,
                  gatedContent.SubscriptionEncryptedPollWeightTokenPublicKey,
                )
              : "",
          ]).then(([PollWeightType, PollOptions, PollWeightTokenPublicKey]) => {
            const data = {
              PollWeightType,
              PollOptions,
              PollWeightTokenPublicKey,
            };
            newCacheEntry.pollData = data;
            setPollData(data);
          }),
        );
      }

      await Promise.all(promises);
      decryptedGatedContentCache.set(cacheKey, newCacheEntry);
    },
    [cacheKey, gatedContent, decryptedGatedContentCache],
  );

  useEffect(() => {
    if (
      !isLoggedIn ||
      userLoading ||
      !currentUserPublicKey ||
      subscriptions.size === 0 ||
      !gatedContent?.SubscriptionAccessGroupKeyName ||
      !creator?.publicKey ||
      cacheEntry
    ) {
      return;
    }

    const seedHex = getDecryptionSeedHex({
      creatorPublicKey: creator.publicKey,
      accessGroupKeyName: gatedContent?.SubscriptionAccessGroupKeyName,
      subscriberPublicKey: currentUserPublicKey,
    });

    if (seedHex) {
      const newCacheEntry =
        decryptedGatedContentCache.get(cacheKey) ??
        ({} as DecryptedGatedContentCacheEntry);

      decryptedGatedContentCache.set(cacheKey, {
        ...newCacheEntry,
        isSubscribed: true,
      });

      setViewerIsSubscribed(true);
      setIsPendingContentDecryption(true);
      setGatedContentDecryptError(null);
      decryptContent(seedHex)
        .catch((e) => {
          setGatedContentDecryptError(parseError(e));
        })
        .finally(() => setIsPendingContentDecryption(false));
    }
  }, [
    subscriptions,
    getDecryptionSeedHex,
    creator.publicKey,
    gatedContent,
    currentUserPublicKey,
    decryptContent,
    isLoggedIn,
    userLoading,
    cacheEntry,
    cacheKey,
    decryptedGatedContentCache,
  ]);

  useEffect(() => {
    if (
      cacheEntry ||
      !gatedContent ||
      !isLoggedIn ||
      userLoading ||
      !currentUserPublicKey
    ) {
      return;
    }

    (async function handleGatedContent() {
      const maybeExistingSeedHex = getDecryptionSeedHex({
        creatorPublicKey: gatedContent.SubscriptionAccessGroupOwnerPublicKey,
        accessGroupKeyName: gatedContent.SubscriptionAccessGroupKeyName,
        subscriberPublicKey: currentUserPublicKey,
      });

      if (maybeExistingSeedHex) {
        const newCacheEntry =
          decryptedGatedContentCache.get(cacheKey) ??
          ({} as DecryptedGatedContentCacheEntry);

        decryptedGatedContentCache.set(cacheKey, {
          ...newCacheEntry,
          isSubscribed: true,
        });

        setViewerIsSubscribed(true);
        setIsPendingContentDecryption(true);
        setGatedContentDecryptError(null);

        try {
          await decryptContent(maybeExistingSeedHex);
        } catch (e) {
          setGatedContentDecryptError(parseError(e));
        }

        setIsPendingContentDecryption(false);
        return;
      }

      setIsPendingContentDecryption(true);
      setGatedContentDecryptError(null);

      try {
        const request = getEncryptedPrivateKey({
          variables: {
            accessGroupOwnerPublicKey:
              gatedContent.SubscriptionAccessGroupOwnerPublicKey,
            accessGroupKeyName: gatedContent.SubscriptionAccessGroupKeyName,
            accessGroupMemberPublicKey: currentUserPublicKey,
            accessGroupMemberKeyName: DEFAULT_MESSAGING_GROUP_KEY_NAME,
          },
        });
        // Apollo 4 can abort prior lazy-query executions when this effect reruns.
        request.retain?.();

        const { data } = await request;

        const encryptedSeedHexWithoutHexPrefix =
          data?.accessGroupMember?.encryptedKey?.slice(2);

        if (!encryptedSeedHexWithoutHexPrefix) {
          // If the user doesn't have an encrypted access group key, it means
          // they haven't subscribed to the tier that allows them to view this content.
          // We can't decrypt the content, so we'll just return early.
          setViewerIsSubscribed(false);
          setIsPendingContentDecryption(false);
          return;
        }

        setViewerIsSubscribed(true);

        const { seedHex } = await identity.decryptAccessGroupKeyPair(
          encryptedSeedHexWithoutHexPrefix,
        );

        addDecryptionSeedHexes([
          {
            creatorPublicKey:
              gatedContent.SubscriptionAccessGroupOwnerPublicKey,
            accessGroupKeyName: gatedContent.SubscriptionAccessGroupKeyName,
            subscriberPublicKey: currentUserPublicKey,
            decryptionSeedHex: seedHex,
          },
        ]);
      } catch (e) {
        if (!isAbortError(e)) {
          setGatedContentDecryptError(parseError(e));
        }
      }

      setIsPendingContentDecryption(false);
    })();
  }, [
    decryptContent,
    cacheEntry,
    cacheKey,
    decryptedGatedContentCache,
    gatedContent,
    getEncryptedPrivateKey,
    postExtraData,
    currentUserPublicKey,
    addDecryptionSeedHexes,
    getDecryptionSeedHex,
    isLoggedIn,
    userLoading,
  ]);

  useEffect(() => {
    setGatedContent(parsePostGatedContent(postExtraData));
  }, [postExtraData]);

  if (children) {
    return children({
      gatedContent,
      decryptedPostBody,
      decryptedImageURLs,
      decryptedVideoURLs,
      decryptedAudioURLs,
      isPendingContentDecryption,
      viewerIsSubscribed,
      gatedContentDecryptError,
      pollData,
    });
  }

  if (!gatedContent) {
    return null;
  }

  if (gatedContentDecryptError) {
    return (
      <div className="text-red-500">
        There was a problem decrypting this post: {gatedContentDecryptError}
      </div>
    );
  }

  const isOneTimeType =
    getAccessGroupType(gatedContent.SubscriptionAccessGroupKeyName) ===
    AccessGroupType.OneTime;

  const subscriptionCount =
    post?.subscriptionStats?.nodes?.[0]?.subscriptionCount;
  const totalPaidUsdCents =
    post?.subscriptionStats?.nodes?.[0]?.totalPaidUsdCents;
  const totalEarnedText = (
    <>
      {!!totalPaidUsdCents && !!subscriptionCount && (
        <div className="flex flex-col text-xs">
          <span>Total Earnings:</span>
          <div
            className="
              flex flex-row items-end gap-1
              sm:flex-row sm:items-center
            "
          >
            <span>{formatUSDCents(Number(totalPaidUsdCents))}</span>
            <div>
              ({subscriptionCount}{" "}
              <div
                className="
                  hidden
                  sm:inline-block
                "
              >
                {pluralize(
                  Number(subscriptionCount),
                  isOneTimeType ? "unlock" : "subscribe",
                  {
                    includeCount: false,
                  },
                )}
              </div>
              <div
                className="
                  inline-block
                  sm:hidden
                "
              >
                {pluralize(
                  Number(subscriptionCount),
                  isOneTimeType ? "unlock" : "sub",
                  {
                    includeCount: false,
                  },
                )}
              </div>
              )
            </div>
          </div>
        </div>
      )}
    </>
  );

  if (
    (!(userLoading || isPendingContentDecryption) && !viewerIsSubscribed) ||
    !isLoggedIn
  ) {
    return (
      <GatedContentDialog
        creator={creator}
        blurHash={gatedContent.SubscriptionBlurHash}
        accessGroupKeyName={gatedContent.SubscriptionAccessGroupKeyName}
        priceToUnlockUsdCents={gatedContent.PriceToUnlockUsdCents}
        postHash={postHash}
      >
        {!!totalPaidUsdCents && !!subscriptionCount && (
          <div className="relative mx-auto pt-1 text-muted">
            {totalEarnedText}
          </div>
        )}
      </GatedContentDialog>
    );
  }

  return (
    <div className="rounded-md border border-primary">
      {userLoading || isPendingContentDecryption ? (
        // Don't increase it from 160px. The placeholder needs to match the
        // minimum possible height of the decrypted content.
        <Skeleton className="h-[160px] w-full rounded-2xl">
          <div className="flex h-full items-center justify-center">
            {viewerIsSubscribed && (
              <LuUnlock size={24} className="text-muted" />
            )}
          </div>
        </Skeleton>
      ) : (
        <>
          <div className="p-2">
            <div className="flex items-center gap-2 p-2">
              <Badge className="inline-flex items-center gap-2 font-sans">
                <LuUnlock /> Unlocked
              </Badge>
            </div>
            {decryptedPostBody && (
              <div className="p-2">
                <PostBody
                  body={decryptedPostBody}
                  className="wrap-break-word whitespace-pre-line"
                />
              </div>
            )}
            {decryptedPostBody && (
              <div className="p-2">
                <EmbedPlayer
                  url={[postExtraData.EmbedVideoURL, decryptedPostBody]}
                  disabledURLs={
                    postExtraData?.DisabledEmbedURLs
                      ? JSON.parse(postExtraData?.DisabledEmbedURLs)
                      : undefined
                  }
                />
              </div>
            )}
            {decryptedImageURLs.length > 0 && (
              <div className="p-2">
                <PostImageSection
                  mediaMeta={mediaMeta}
                  urls={decryptedImageURLs}
                  postHash={isPureRepost ? repost?.postHash : postHash}
                />
              </div>
            )}
            {decryptedVideoURLs.length > 0 && (
              <div className="p-2">
                <PostVideoSection
                  videoUrls={decryptedVideoURLs}
                  postHash={isPureRepost ? repost?.postHash : postHash}
                />
              </div>
            )}
            {decryptedAudioURLs.length > 0 && (
              <div className="flex w-full flex-col gap-2 p-2">
                {decryptedAudioURLs.map((url: string) => {
                  return (
                    <PostAudioPlayer
                      key={url}
                      src={url}
                      mediaMeta={mediaMeta}
                      postHash={isPureRepost ? repost?.postHash : postHash}
                    />
                  );
                })}
              </div>
            )}
            {pollData && <PostPoll extraData={pollData} />}
          </div>
          <div
            className="
              flex w-full items-center justify-between border-t
              border-border-light p-4 text-xs text-muted
            "
          >
            <div className="w-3/5">
              {getAccessGroupType(
                gatedContent.SubscriptionAccessGroupKeyName,
              ) === AccessGroupType.OneTime ? (
                creator.publicKey === currentUserPublicKey ? (
                  `You are charging a one-time fee of ${formatUSDCents(gatedContent.PriceToUnlockUsdCents)} to unlock this post`
                ) : (
                  <div>
                    You unlocked this
                    <br />
                    post for{" "}
                    {formatUSDCents(gatedContent.PriceToUnlockUsdCents)}
                  </div>
                )
              ) : creator.publicKey === currentUserPublicKey ? (
                `You are charging a monthly fee of ${formatUSDCents(gatedContent.PriceToUnlockUsdCents)} to unlock this post`
              ) : (
                <div>
                  You've subscribed to <br />
                  {getUserName({ account: creator, truncateLength: 15 })}'s tier
                </div>
              )}
            </div>
            <div className="text-right">{totalEarnedText}</div>
          </div>
        </>
      )}
    </div>
  );
};
